import type { AdminMembership, AdminUserDetail } from '@fruuit/shared'
import { Badge, Button, Callout, Drawer, Select, Skeleton, StatusBadge, useToast } from '@fruuit/ui'
import { Eye, KeyRound, LogOut, RotateCcw, Send, UserX } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { errorMessage } from '../../api/client'
import { useApiMutation } from '../../api/hooks'
import { useSession } from '../../auth/session-context'
import { formatDateTime, METHOD_LABELS, ROLE_LABELS } from '../auth/format'
import { useStepUp } from '../auth/StepUp'
import { adminApi, adminKeys, useAdminUser } from './api'
import { ReasonDialog } from './ReasonDialog'
import type { ViewAsTarget } from './ViewAsDialog'

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-line pt-5 first:border-t-0 first:pt-0">
      <h3 className="mb-3 text-xs font-semibold tracking-[0.06em] text-sage uppercase">{title}</h3>
      {children}
    </section>
  )
}

type Pending = 'deactivate' | 'reactivate' | 'reset-mfa' | 'revoke-sessions' | null

/** One user's access, sign-in methods and account actions (P-ADM-007). */
export function UserDrawer({
  userId,
  onClose,
  onViewAs,
}: {
  userId: string | null
  onClose: () => void
  onViewAs: (target: ViewAsTarget) => void
}) {
  const me = useSession()
  const operator = me.platform_role === 'platform_operator'
  const detail = useAdminUser(userId)
  const { toast } = useToast()
  const { guard, dialog } = useStepUp()
  const [confirm, setConfirm] = useState<Pending>(null)
  const invalidate = [adminKeys.users, adminKeys.orgs, adminKeys.staff]
  const done = (message: string) => () => {
    setConfirm(null)
    toast({ tone: 'success', message })
  }
  const fail = (error: Error) => toast({ tone: 'error', message: errorMessage(error) })

  const setRole = useApiMutation(
    (vars: Parameters<typeof adminApi.setRole>[0], ctx) => guard(() => adminApi.setRole(vars, ctx)),
    { invalidate, onSuccess: done('Role updated.'), onError: fail },
  )
  const deactivate = useApiMutation(
    (vars: Parameters<typeof adminApi.deactivate>[0], ctx) =>
      guard(() => adminApi.deactivate(vars, ctx)),
    { invalidate, onSuccess: done('User deactivated. Their sessions ended.') },
  )
  const reactivate = useApiMutation(
    (vars: Parameters<typeof adminApi.reactivate>[0], ctx) =>
      guard(() => adminApi.reactivate(vars, ctx)),
    { invalidate, onSuccess: done('User reactivated.') },
  )
  const resetMfa = useApiMutation(
    (vars: Parameters<typeof adminApi.resetMfa>[0], ctx) =>
      guard(() => adminApi.resetMfa(vars, ctx)),
    { invalidate, onSuccess: done('Two-factor authentication was reset.') },
  )
  const revokeSessions = useApiMutation(
    (id: string, ctx) => guard(() => adminApi.revokeSessions(id, ctx)),
    { invalidate, onSuccess: done('All sessions were signed out.') },
  )
  const resend = useApiMutation((id: string, ctx) => guard(() => adminApi.resendInvite(id, ctx)), {
    invalidate,
    onSuccess: () => toast({ tone: 'success', message: 'Invitation sent again with a new link.' }),
    onError: fail,
  })
  const revokeInvite = useApiMutation(
    (id: string, ctx) => guard(() => adminApi.revokeInvite(id, ctx)),
    {
      invalidate,
      onSuccess: () => toast({ tone: 'success', message: 'Invitation revoked.' }),
      onError: fail,
    },
  )

  const user = detail.data
  const title = user ? user.display_name : 'User'

  return (
    <Drawer open={userId !== null} onClose={onClose} title={title}>
      {detail.isPending ? (
        <div role="status" aria-label="Loading user" className="flex flex-col gap-3 p-5">
          <Skeleton className="h-6 w-2/3" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : detail.isError ? (
        <Callout tone="warning" className="m-5">
          {errorMessage(detail.error)}
        </Callout>
      ) : user ? (
        <div className="flex flex-col gap-6 px-5 py-5">
          <Header user={user} />
          {user.can_impersonate && operator && !me.impersonated_by ? (
            <Button
              icon={<Eye />}
              onClick={() =>
                onViewAs({ id: user.id, display_name: user.display_name, email: user.email })
              }
            >
              View as {user.display_name.split(' ')[0]}
            </Button>
          ) : null}

          <Section title="Organisations">
            {user.memberships.length === 0 ? (
              <p className="text-sm text-sage">No organisation memberships.</p>
            ) : (
              <ul className="flex flex-col gap-3">
                {user.memberships.map((m) => (
                  <MembershipRow
                    key={`${m.org_id}-${m.role}`}
                    membership={m}
                    busy={setRole.isPending}
                    onChange={(role) => setRole.mutate({ userId: user.id, org_id: m.org_id, role })}
                  />
                ))}
              </ul>
            )}
          </Section>

          {user.pending_invites.length > 0 ? (
            <Section title="Pending invitations">
              <ul className="flex flex-col gap-3">
                {user.pending_invites.map((invite) => (
                  <li key={invite.id} className="rounded-lg border border-line p-3 text-sm">
                    <p className="font-medium text-ink">
                      {invite.org_name} · {ROLE_LABELS[invite.role] ?? invite.role}
                    </p>
                    <p className="mt-0.5 text-xs text-sage">
                      Sent {formatDateTime(invite.resent_at ?? invite.created_at)} · expires{' '}
                      {formatDateTime(invite.expires_at)}
                    </p>
                    <div className="mt-2 flex gap-2">
                      <Button
                        size="sm"
                        variant="secondary"
                        icon={<Send />}
                        onClick={() => resend.mutate(invite.id)}
                      >
                        Resend
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => revokeInvite.mutate(invite.id)}
                      >
                        Revoke
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}

          <Section title="Sign-in">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <dt className="text-sage">Last sign-in</dt>
              <dd className="text-ink">{formatDateTime(user.last_sign_in_at)}</dd>
              <dt className="text-sage">Active sessions</dt>
              <dd className="text-ink">{user.active_sessions}</dd>
              <dt className="text-sage">Two-factor</dt>
              <dd className="text-ink">
                {user.mfa_enrolled ? `On since ${formatDateTime(user.mfa_enrolled_at)}` : 'Off'}
              </dd>
            </dl>
            <h4 className="mt-4 mb-2 text-sm font-semibold text-ink">Linked identities</h4>
            {user.identities.length === 0 ? (
              <p className="text-sm text-sage">None yet. The first sign-in links one.</p>
            ) : (
              <ul className="flex flex-col gap-2">
                {user.identities.map((identity) => (
                  <li key={identity.id} className="rounded-lg border border-line px-3 py-2 text-sm">
                    <p className="font-medium text-ink">
                      {METHOD_LABELS[identity.provider] ?? identity.provider}{' '}
                      <span className="font-normal text-sage">· {identity.email}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-sage">
                      Linked {formatDateTime(identity.linked_at)}
                      {identity.last_used_at
                        ? ` · last used ${formatDateTime(identity.last_used_at)}`
                        : ''}
                      {identity.tid ? ` · tenant ${identity.tid.slice(0, 8)}…` : ''}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="secondary"
                icon={<LogOut />}
                disabled={user.active_sessions === 0}
                onClick={() => setConfirm('revoke-sessions')}
              >
                Sign out everywhere
              </Button>
              <Button
                size="sm"
                variant="secondary"
                icon={<KeyRound />}
                disabled={!user.mfa_enrolled}
                onClick={() => setConfirm('reset-mfa')}
              >
                Reset two-factor
              </Button>
            </div>
          </Section>

          <Section title="Account">
            {user.status === 'deactivated' ? (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-sage">
                  Deactivated {formatDateTime(user.deactivated_at)}
                  {user.deactivation_reason ? `: ${user.deactivation_reason}` : ''}
                </p>
                <div>
                  <Button size="sm" icon={<RotateCcw />} onClick={() => setConfirm('reactivate')}>
                    Reactivate
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                size="sm"
                variant="danger"
                icon={<UserX />}
                disabled={user.id === me.user.id || (user.platform_role !== null && !operator)}
                onClick={() => setConfirm('deactivate')}
              >
                Deactivate
              </Button>
            )}
          </Section>

          <Confirmations
            user={user}
            pending={confirm}
            close={() => setConfirm(null)}
            deactivate={deactivate}
            reactivate={reactivate}
            resetMfa={resetMfa}
            revokeSessions={revokeSessions}
          />
        </div>
      ) : null}
      {dialog}
    </Drawer>
  )
}

function Header({ user }: { user: AdminUserDetail }) {
  return (
    <div>
      <p className="text-sm text-sage">{user.email}</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <StatusBadge status={user.status} />
        {user.platform_role ? (
          <Badge tone="brand">{ROLE_LABELS[user.platform_role] ?? user.platform_role}</Badge>
        ) : null}
        {user.mfa_enrolled ? <Badge tone="positive">2FA on</Badge> : null}
      </div>
    </div>
  )
}

function MembershipRow({
  membership: m,
  busy,
  onChange,
}: {
  membership: AdminMembership
  busy: boolean
  onChange: (role: 'investor' | 'investor_admin' | null) => void
}) {
  const label = ROLE_LABELS[m.role] ?? m.role
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-line px-3 py-2.5">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-ink">{m.org_name}</p>
        <p className="text-xs text-sage">
          {m.org_kind === 'fruuit' ? 'Fruuit staff' : 'Investor organisation'}
          {m.org_status === 'suspended' ? ' · suspended' : ''}
        </p>
      </div>
      {m.org_kind === 'investor' && m.status === 'active' ? (
        <Select
          label={`Role in ${m.org_name}`}
          hideLabel
          className="w-44"
          value={m.role}
          disabled={busy}
          onChange={(event) => {
            const value = event.target.value
            onChange(value === 'remove' ? null : (value as 'investor' | 'investor_admin'))
          }}
          options={[
            { value: 'investor', label: 'Investor' },
            { value: 'investor_admin', label: 'Investor admin' },
            { value: 'remove', label: 'Remove from org' },
          ]}
        />
      ) : (
        <Badge tone={m.status === 'active' ? 'outline' : 'quiet'}>
          {m.status === 'active' ? label : `${label} (revoked)`}
        </Badge>
      )}
    </li>
  )
}

type Mutation<V> = {
  mutate: (vars: V) => void
  isPending: boolean
  error: Error | null
}

function Confirmations({
  user,
  pending,
  close,
  deactivate,
  reactivate,
  resetMfa,
  revokeSessions,
}: {
  user: AdminUserDetail
  pending: Pending
  close: () => void
  deactivate: Mutation<{ userId: string; revision: number; reason: string }>
  reactivate: Mutation<{ userId: string; revision: number }>
  resetMfa: Mutation<{ userId: string; revision: number }>
  revokeSessions: Mutation<string>
}) {
  const err = (m: Mutation<never>) => (m.error ? errorMessage(m.error) : undefined)
  return (
    <>
      <ReasonDialog
        open={pending === 'deactivate'}
        title={`Deactivate ${user.display_name}?`}
        description="They are signed out everywhere and can't sign in until reactivated. Nothing is deleted."
        confirmLabel="Deactivate"
        variant="danger"
        withReason
        pending={deactivate.isPending}
        error={err(deactivate as Mutation<never>)}
        onConfirm={(reason) =>
          deactivate.mutate({ userId: user.id, revision: user.revision, reason })
        }
        onClose={close}
      />
      <ReasonDialog
        open={pending === 'reactivate'}
        title={`Reactivate ${user.display_name}?`}
        confirmLabel="Reactivate"
        pending={reactivate.isPending}
        error={err(reactivate as Mutation<never>)}
        onConfirm={() => reactivate.mutate({ userId: user.id, revision: user.revision })}
        onClose={close}
      />
      <ReasonDialog
        open={pending === 'reset-mfa'}
        title="Reset two-factor authentication?"
        description="Their authenticator and recovery codes stop working. They can set up a new one from their account page."
        confirmLabel="Reset"
        variant="danger"
        pending={resetMfa.isPending}
        error={err(resetMfa as Mutation<never>)}
        onConfirm={() => resetMfa.mutate({ userId: user.id, revision: user.revision })}
        onClose={close}
      />
      <ReasonDialog
        open={pending === 'revoke-sessions'}
        title="Sign this user out everywhere?"
        description={`${user.active_sessions} active session${user.active_sessions === 1 ? '' : 's'} will end immediately.`}
        confirmLabel="Sign out everywhere"
        variant="danger"
        pending={revokeSessions.isPending}
        error={err(revokeSessions as Mutation<never>)}
        onConfirm={() => revokeSessions.mutate(user.id)}
        onClose={close}
      />
    </>
  )
}
